<template>
  <div class="workspace-shell employee-shell">
    <aside class="employee-side">
      <div class="brand-block">
        <p>Staff Console</p>
        <h2>办公用品申领</h2>
        <span>{{ session.user.company_name }}</span>
      </div>

      <el-menu :default-active="activeMenu" class="nav-menu" @select="handleMenuSelect">
        <el-menu-item index="supplies">
          <el-icon><Box /></el-icon>
          <span>用品浏览</span>
        </el-menu-item>
        <el-menu-item index="requests">
          <el-icon><Tickets /></el-icon>
          <span>我的申领</span>
        </el-menu-item>
        <el-menu-item index="notifications">
          <el-icon><Bell /></el-icon>
          <span>站内信</span>
        </el-menu-item>
        <el-menu-item index="profile">
          <el-icon><UserFilled /></el-icon>
          <span>个人信息</span>
        </el-menu-item>
      </el-menu>
    </aside>

    <main class="workspace-main">
      <header class="top-bar">
        <div>
          <p class="page-kicker">员工工作台</p>
          <h1>{{ titleMap[activeMenu] }}</h1>
        </div>
        <WorkspaceActionBar
          class="top-actions"
          :unread-count="unreadCount"
          :username="session.user.username"
          role-label="企业员工"
          @notifications="activeMenu = 'notifications'"
          @profile="activeMenu = 'profile'"
          @logout="$emit('logout')"
        />
      </header>

      <EmployeeSuppliesPanel v-if="activeMenu === 'supplies'" />
      <EmployeeRequestsPanel v-else-if="activeMenu === 'requests'" />
      <NotificationsPanel
        v-else-if="activeMenu === 'notifications'"
        @unread-change="handleUnreadChange"
      />
      <ProfilePanel
        v-else-if="activeMenu === 'profile'"
        :session="session"
        role-label="员工"
        @session-updated="$emit('session-updated', $event)"
      />
    </main>
  </div>
</template>

<script>
import { Bell, Box, Tickets, UserFilled } from '@element-plus/icons-vue'
import { fetchUserNotificationUnreadCount } from '../services/notificationService'
import EmployeeRequestsPanel from './employee/EmployeeRequestsPanel.vue'
import EmployeeSuppliesPanel from './employee/EmployeeSuppliesPanel.vue'
import NotificationsPanel from './shared/NotificationsPanel.vue'
import ProfilePanel from './shared/ProfilePanel.vue'
import WorkspaceActionBar from './WorkspaceActionBar.vue'

export default {
  name: 'EmployeeWorkspace',
  components: {
    Bell,
    Box,
    EmployeeRequestsPanel,
    EmployeeSuppliesPanel,
    NotificationsPanel,
    ProfilePanel,
    Tickets,
    UserFilled,
    WorkspaceActionBar
  },
  props: {
    session: {
      type: Object,
      required: true
    }
  },
  emits: ['logout', 'session-updated'],
  data() {
    return {
      activeMenu: 'supplies',
      unreadCountTimer: null,
      unreadCount: 0,
      titleMap: {
        supplies: '用品浏览',
        requests: '我的申领',
        notifications: '站内信',
        profile: '个人信息'
      }
    }
  },
  mounted() {
    this.startUnreadCountPolling()
  },
  beforeUnmount() {
    this.stopUnreadCountPolling()
  },
  methods: {
    handleMenuSelect(index) {
      this.activeMenu = index
    },
    handleUnreadChange(count) {
      this.unreadCount = count
    },
    startUnreadCountPolling() {
      this.loadUnreadCount()
      this.stopUnreadCountPolling()
      this.unreadCountTimer = window.setInterval(() => {
        this.loadUnreadCount()
      }, 60000)
    },
    stopUnreadCountPolling() {
      if (this.unreadCountTimer) {
        window.clearInterval(this.unreadCountTimer)
        this.unreadCountTimer = null
      }
    },
    async loadUnreadCount() {
      const response = await fetchUserNotificationUnreadCount()
      this.unreadCount = response.data?.unreadCount ?? response.data ?? 0
    }
  }
}
</script>

<style scoped>
.employee-shell {
  min-height: 100vh;
  display: grid;
  grid-template-columns: 260px 1fr;
  background: linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%);
}

.employee-side {
  padding: 28px 20px;
  background: linear-gradient(180deg, #102a43 0%, #1f4e79 100%);
  color: #f8fafc;
}

.brand-block {
  padding: 12px 10px 28px;
}

.brand-block p,
.brand-block span {
  margin: 0;
  color: rgba(226, 232, 240, 0.76);
}

.brand-block h2 {
  margin: 8px 0;
  font-size: 28px;
}

.nav-menu {
  background: transparent;
  border-right: none;
}

.nav-menu :deep(.el-menu-item) {
  position: relative;
  margin-bottom: 8px;
  min-height: 52px;
  border-radius: 16px;
  color: #dbeafe;
  font-weight: 600;
  transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
}

.nav-menu :deep(.el-menu-item .el-icon) {
  margin-right: 10px;
  font-size: 18px;
  color: rgba(219, 234, 254, 0.78);
  transition: color 0.2s ease;
}

.nav-menu :deep(.el-menu-item:hover) {
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  transform: translateX(4px);
}

.nav-menu :deep(.el-menu-item:hover .el-icon) {
  color: #fff;
}

.nav-menu :deep(.el-menu-item.is-active) {
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  box-shadow: inset 0 0 0 1px rgba(191, 219, 254, 0.14);
}

.nav-menu :deep(.el-menu-item.is-active .el-icon) {
  color: #fff;
}

.nav-menu :deep(.el-menu-item.is-active::before) {
  content: '';
  position: absolute;
  left: 10px;
  top: 12px;
  bottom: 12px;
  width: 4px;
  border-radius: 999px;
  background: #bfdbfe;
}

.workspace-main {
  padding: 28px;
}

.top-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 22px;
}

.page-kicker {
  margin: 0;
  color: #64748b;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.top-bar h1 {
  margin: 10px 0 0;
  font-size: 34px;
  color: #0f172a;
}

.top-actions {
  flex-shrink: 0;
}

@media (max-width: 1100px) {
  .employee-shell {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .workspace-main {
    padding: 18px 12px 28px;
  }

  .top-bar {
    flex-direction: column;
    align-items: flex-start;
  }

  .top-bar h1 {
    font-size: 28px;
  }

  .top-actions {
    width: 100%;
  }
}
</style>
